import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChildren, ElementRef, QueryList, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { CrudTableViewColumn, CrudViewColumnType } from '../../../utils/list-grid';
import { GenerateExampleDataService } from '../utils/services/generate-example-data/generate-example-data.service';
import { IconType } from '../../../utils/icon';
import { ButtonSize } from '../../../utils/button';

@Component({
  selector: 'rappider-grid-builder-columns',
  templateUrl: './grid-builder-columns.component.html',
  styleUrls: ['./grid-builder-columns.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GridBuilderColumnsComponent),
      multi: true
    }
  ]
})
export class GridBuilderColumnsComponent implements OnInit, ControlValueAccessor, AfterViewInit {

  @Output() populatedDataChange = new EventEmitter<Record<string, unknown>[]>();
  @Output() editColumnRequest = new EventEmitter<{ column: CrudTableViewColumn; index: number }>();
  @Output() columnDeleted = new EventEmitter<void>();

  @Input() set resetSelection(value: boolean) {
    if (value) {
      this.resetSelectedColumnIndex();
    }
  }

  @ViewChildren('columnItem') columnItems: QueryList<ElementRef>;

  _value: CrudTableViewColumn[];
  initialCrudFormItem: any = {
    type: undefined,
    title: undefined,
    fieldName: undefined,
    inputTemplateType: undefined,
    visible: true,
    width: 50
  };

  editButtonIconConfig = {
    type: IconType.FontAwesome,
    name: 'fa-light fa-pen-to-square'
  };

  deleteButtonIconConfig = {
    type: IconType.FontAwesome,
    name: 'fa-sharp fa-solid fa-trash'
  };

  /* populated example data from field type */
  populatedData: Record<string, unknown>[] = [];

  selectedColumnIndex: number;

  ButtonSize = ButtonSize;

  get value() {
    return this._value;
  }

  set value(value: CrudTableViewColumn[]) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  constructor(
    private generateExampleDataService: GenerateExampleDataService
  ) { }

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    this.scrollToSelectedColumn();
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: CrudTableViewColumn[]) {
    if (!value) {
      this._value = [];
    } else {
      this._value = cloneDeep(value);
      this.populateData();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  addColumn() {
    const newColumnIndex = this.value.length;
    this.value.push(cloneDeep(this.initialCrudFormItem));
    this.onChange(this.value);

    this.selectedColumnIndex = newColumnIndex;

    setTimeout(() => {
      this.editColumnRequest.emit({
        column: this.value[newColumnIndex],
        index: newColumnIndex
      });
      this.scrollToSelectedColumn();
    }, 0);
  }

  onColumnClick(index: number) {
    this.selectedColumnIndex = index;
    this.editColumnRequest.emit({
      column: this.value[index],
      index: index
    });
    setTimeout(() => {
      this.scrollToSelectedColumn();
    }, 0);
  }

  onDeleteItemClick(deletedItemIndex: number) {
    this.value = this.value.filter((item, index) => index !== deletedItemIndex);

    if (deletedItemIndex === this.selectedColumnIndex) {
      this.resetSelectedColumnIndex();
      this.columnDeleted.emit();
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.value, event.previousIndex, event.currentIndex);
    this.onChange(this.value);
  }

  populateData() {
    this.populatedData = this.generateExampleDataService.generateExampleDataFromCrudTableColumns(this.value);
    this.populatedDataChange.emit(this.populatedData);
  }

  resetSelectedColumnIndex() {
    this.selectedColumnIndex = null;
  }

  scrollToSelectedColumn() {
    if (this.columnItems && this.selectedColumnIndex != null) {
      const columnItem = this.columnItems.toArray()[this.selectedColumnIndex]?.nativeElement;
      if (columnItem) {
        columnItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }
}
